import {
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { DateTime } from 'luxon';
import { Subscription } from 'rxjs';
import { translateKpiReportFilterFromFilterDTO } from 'src/app/common/dtos/translators/report-filters.translator';
import { EnvironmentService } from 'src/app/common/services/environment/environment.service';
import { User } from 'src/app/common/state/user/user.model';
import { UserState } from 'src/app/common/state/user/user.state';
import { PaginationDTO } from 'src/app/common/types/responses/responses-template';
import { checkIfB2b, checkIfE2L } from 'src/app/common/utilities/role-helpers';
import {
  KpiReportFilter,
  ReportingPdSessionAttendanceItemDTO,
  ReportingPdSessionSummaryItemDTO,
} from '../../../common/dtos/reports.dto';
import { toHoursAndMinutes } from '../../../common/utilities/time-helpers';
import {
  SortEvent,
  SortableHeader,
} from '../../shared/directives/sortable-header.directive';
import { ReportPageFiltersService } from '../../shared/services/report/report-page-filters.service';
import { ReportPdSessionSummaryService } from '../../shared/services/report/report-pd-session-summary.service';
import { ReportService } from '../../shared/services/report/report.service';

interface SessionSummarySortIcon {
  session_name: 'gray' | 'none';
  session_date: 'gray' | 'none';
  facilitators: 'gray' | 'none';
  session_type: 'gray' | 'none';
  percent_present: 'gray' | 'none';
  registered_count: 'gray' | 'none';
  attended_count: 'gray' | 'none';
  waitlist_count: 'gray' | 'none';
  is_completed: 'gray' | 'none';
  competencies: 'gray' | 'none';
}

interface SessionAttendanceSortIcon {
  user_name: 'gray' | 'none';
  school: 'gray' | 'none';
  cohorts: 'gray' | 'none';
  status: 'gray' | 'none';
}

const BASE_URL = EnvironmentService.apiUrl();

@Component({
  selector: 'app-reporting-pd-session-summary',
  templateUrl: './reporting-pd-session-summary.component.html',
  styleUrl: './reporting-pd-session-summary.component.scss',
  standalone: false,
})
export class ReportingPdSessionSummaryComponent implements OnInit, OnDestroy {
  subs: Subscription[] = [];

  canFilterByDistrict = false;

  isLoading = false;

  drilldownIsLoading = false;

  disableDownload = false;

  noResults = false;

  @ViewChildren(SortableHeader) headers: QueryList<SortableHeader>;

  @ViewChild('p') csvDownloadingPopover: NgbPopover;

  pdSessionId = 0;

  tableData: ReportingPdSessionSummaryItemDTO[];

  drillDownTableData: ReportingPdSessionAttendanceItemDTO[];

  sessionName = '';

  sessionNameShortened = '';

  listMeta: PaginationDTO;

  filters: KpiReportFilter[];

  reportFiltersetId?: number | null;

  sortIconTemp: SessionSummarySortIcon = {
    session_name: 'gray',
    session_date: 'gray',
    facilitators: 'gray',
    session_type: 'gray',
    percent_present: 'gray',
    registered_count: 'gray',
    attended_count: 'gray',
    waitlist_count: 'gray',
    is_completed: 'gray',
    competencies: 'gray',
  };

  sortIconTempDrilldown: SessionAttendanceSortIcon = {
    user_name: 'gray',
    school: 'gray',
    cohorts: 'gray',
    status: 'gray',
  };

  sortBy = '';

  sortByDesc = 0;

  column = '';

  direction = '';

  lastUpdatedDate: string;

  constructor(
    private store: Store,
    private reportPdSessionSummaryService: ReportPdSessionSummaryService,
    private reportFiltersService: ReportPageFiltersService,
    private reportService: ReportService
  ) {
    const user = this.store.selectSnapshot(UserState.getUser) as User;
    this.canFilterByDistrict = checkIfE2L(user) || checkIfB2b(user);
  }

  ngOnInit() {
    this.subs.push(
      this.reportFiltersService.filters.pipe().subscribe((filters) => {
        if (filters.reportName && filters.reportName !== 'pd_session_summary') {
          return;
        }
        if (filters.schoolYearFilter) {
          this.filters = translateKpiReportFilterFromFilterDTO(filters);
          this.reportFiltersetId = filters.reportFiltersetId;
          this.pdSessionId = 0;
          this.getPdSessionSummaryItems(this.filters);
        }
      })
    );
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => sub.unsubscribe());
  }

  downloadCSV(): void {
    this.disableDownload = true;

    if (
      this.filters.indexOf({
        codename: 'pd_session',
        value: [this.pdSessionId.toString()],
      }) === -1
    ) {
      this.filters.push({
        codename: 'pd_session',
        value: [this.pdSessionId.toString()],
      });
    }

    this.reportService
      .generateReportCSV('pdStaffMemberSessionAttendanceDownload', this.filters)
      .subscribe({
        error: () => {
          this.csvDownloadingPopover.close();
          this.noResults = true;
          this.disableDownload = false;
        },
        next: (res) => {
          if (res.has_results) {
            // res.file_name should always start with a slash, but need to be sure for the link to work properly
            if (!res.file_name.startsWith('/')) {
              res.file_name = `/${res.file_name}`;
            }
            window.open(`${BASE_URL}${res.file_name}`, '_blank');
          } else {
            this.noResults = true;
          }
          this.disableDownload = false;
        },
      });
  }

  getPdSessionSummaryItems(filters: KpiReportFilter[], page?: number) {
    this.isLoading = true;
    this.lastUpdatedDate = DateTime.now().toFormat('MM/dd h:mm a');
    this.reportPdSessionSummaryService
      .getPdSessionSummary(
        filters,
        page,
        this.sortBy,
        this.sortByDesc,
        undefined,
        undefined,
        this.reportFiltersetId
      )
      .subscribe((res) => {
        this.tableData = res.items;
        this.listMeta = res._meta;
        this.isLoading = false;
      });
  }

  getPdSessionAttendanceItems(filters: KpiReportFilter[], page?: number) {
    this.drilldownIsLoading = true;
    this.lastUpdatedDate = DateTime.now().toFormat('MM/dd h:mm a');

    this.filters = this.filters.filter(
      (filter) => filter.codename !== 'pd_session'
    );

    this.reportPdSessionSummaryService
      .getPdSessionAttendance(
        filters,
        page,
        this.sortBy,
        this.sortByDesc,
        undefined,
        undefined,
        this.reportFiltersetId
      )
      .subscribe((res) => {
        this.drillDownTableData = res.items;

        this.sessionName = this.reportPdSessionSummaryService.sessionName;
        this.sessionNameShortened =
          this.sessionName.length > 30
            ? `${this.sessionName.substring(0, 30)}...`
            : this.sessionName;

        this.listMeta = res._meta;
        this.drilldownIsLoading = false;
      });
  }

  selectSession(pdSessionId: number) {
    this.pdSessionId = pdSessionId;
    this.filters.push({
      codename: 'pd_session',
      value: [pdSessionId.toString()],
    });
    this.getPdSessionAttendanceItems(this.filters);
  }

  backToSessionSummary() {
    this.pdSessionId = 0;
    this.getPdSessionSummaryItems(this.filters);
  }

  toHoursAndMinutes = toHoursAndMinutes;

  paginationChange() {
    this.getPdSessionSummaryItems(this.filters, this.listMeta.currentPage);
  }

  onSort({ column, direction }: SortEvent) {
    this.headers.forEach((header) => {
      if (!this.pdSessionId) {
        if (header.sortable !== column) {
          header.direction = '';
          this.sortIconTemp[header.sortable as keyof SessionSummarySortIcon] =
            'gray';
        } else if (header.direction === '') {
          this.sortIconTemp[header.sortable as keyof SessionSummarySortIcon] =
            'gray';
        } else {
          this.sortIconTemp[header.sortable as keyof SessionSummarySortIcon] =
            'none';
        }
      } else if (this.pdSessionId) {
        if (header.sortable !== column) {
          header.direction = '';
          this.sortIconTempDrilldown[
            header.sortable as keyof SessionAttendanceSortIcon
          ] = 'gray';
        } else if (header.direction === '') {
          this.sortIconTempDrilldown[
            header.sortable as keyof SessionAttendanceSortIcon
          ] = 'gray';
        } else {
          this.sortIconTempDrilldown[
            header.sortable as keyof SessionAttendanceSortIcon
          ] = 'none';
        }
      }
    });

    this.sortBy = column;
    this.sortByDesc = 0;
    if (direction === 'asc') {
      this.sortByDesc = 1;
    }
    if (direction === '') {
      this.sortBy = '';
    }

    this.column = column;
    this.direction = direction;

    if (!this.pdSessionId) {
      this.getPdSessionSummaryItems(this.filters);
    } else {
      if (
        this.filters.indexOf({
          codename: 'pd_session',
          value: [this.pdSessionId.toString()],
        }) === -1
      ) {
        this.filters.push({
          codename: 'pd_session',
          value: [this.pdSessionId.toString()],
        });
      }
      this.getPdSessionAttendanceItems(this.filters);
    }
  }
}
